// Blog metadata and utilities

export interface BlogPost {
  slug: string;
  title: string;
  description: string;
  author: string;
  publishDate: string;
  readingTime: string;
  category: string;
  tags: string[];
  image?: string;
  featured?: boolean;
}

// Blog post index - add new posts here
// Posts are backdated over 6 months (June 2024 - December 2024)
export const blogPosts: BlogPost[] = [
  // January 2026 - Most Recent
  {
    slug: 'shopify-agentic-commerce-brand-preparation',
    title: 'Shopify Agentic Commerce: What Brands Need to Know (And How to Prepare)',
    description: 'Shopify Agentic Commerce enables AI shopping agents to browse, recommend, and purchase across all Shopify stores. Learn how it works and what your brand must do to get discovered.',
    author: 'BrandIndex AI Team',
    publishDate: '2026-01-14',
    readingTime: '18 min read',
    category: 'AI Strategy',
    tags: ['Shopify', 'AI Agents', 'Ecommerce', 'Agentic Commerce', 'AI Shopping', 'Product Discovery'],
    featured: true,
  },
  {
    slug: 'shopify-ucp-universal-commerce-protocol',
    title: 'Shopify UCP: The Universal Commerce Protocol Explained',
    description: 'Google and Shopify launched UCP—the open standard powering AI commerce. Learn how the Universal Commerce Protocol enables AI agents to complete purchases on your store.',
    author: 'BrandIndex AI Team',
    publishDate: '2026-01-16',
    readingTime: '12 min read',
    category: 'AI Strategy',
    tags: ['Shopify', 'UCP', 'Universal Commerce Protocol', 'AI Agents', 'Agentic Commerce', 'Google'],
    featured: false,
  },
  // December 2024
  {
    slug: 'google-business-profile-optimization-ai-visibility',
    title: 'Google Business Profile Optimization for AI Visibility',
    description: 'Your Google Business Profile is the foundation of local AI visibility. Learn how to optimize every field, photo, and feature to get recommended by ChatGPT, Gemini, and Claude.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-12-20',
    readingTime: '14 min read',
    category: 'Local Business',
    tags: ['Google Business Profile', 'Local Business', 'AI Visibility', 'GBP', 'Local SEO'],
    featured: false,
  },
  {
    slug: 'beauty-salon-ai-visibility-guide',
    title: 'Beauty & Salon AI Visibility Guide: Getting Recommended by ChatGPT, Claude, and Gemini',
    description: 'Complete guide for hair salons, med spas, nail salons, and day spas to dominate AI-powered local recommendations. Industry-specific strategies that work.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-12-19',
    readingTime: '16 min read',
    category: 'Industry Guides',
    tags: ['Beauty', 'Salon', 'Med Spa', 'Local Business', 'AI Visibility', 'Industry Guide'],
    featured: false,
  },
  {
    slug: 'how-online-reviews-impact-ai-recommendations',
    title: 'How Online Reviews Impact AI Recommendations (And How to Get More)',
    description: 'Reviews are training data for AI assistants. Learn exactly how reviews influence AI recommendations and build a review generation system that boosts visibility.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-12-18',
    readingTime: '13 min read',
    category: 'Local Business',
    tags: ['Reviews', 'Local Business', 'AI Visibility', 'Google Reviews', 'Reputation'],
    featured: false,
  },
  {
    slug: 'ai-visibility-retail-boutique-shops',
    title: 'AI Visibility for Retail & Boutique Shops: Getting Recommended When Shoppers Ask AI',
    description: 'Retail and boutique shops face unique AI visibility challenges. Learn how to position your shop for discovery queries and get recommended by AI assistants.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-12-17',
    readingTime: '15 min read',
    category: 'Industry Guides',
    tags: ['Retail', 'Boutique', 'Local Business', 'AI Visibility', 'Gift Shops', 'Shopping'],
    featured: false,
  },
  {
    slug: 'free-low-budget-ai-visibility-tactics',
    title: 'Free & Low-Budget AI Visibility Tactics for Small Businesses',
    description: 'You don\'t need a big budget for AI visibility. 80% of the highest-impact tactics are completely free. Here\'s your complete guide to AI visibility on a shoestring.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-12-16',
    readingTime: '14 min read',
    category: 'Local Business',
    tags: ['Budget', 'Free', 'Small Business', 'Local Business', 'AI Visibility', 'DIY'],
    featured: false,
  },
  {
    slug: '10-quick-ai-visibility-wins-local-business',
    title: '10 Quick AI Visibility Wins for Busy Local Business Owners',
    description: 'Boost your local business visibility in ChatGPT, Claude, and Gemini with these 10 actionable AI optimization tactics. Each takes under 2 hours and delivers real results.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-12-15',
    readingTime: '12 min read',
    category: 'Local Business',
    tags: ['Local Business', 'AI Optimization', 'Quick Wins', 'ChatGPT', 'Small Business'],
    featured: true,
  },
  {
    slug: 'future-of-brand-discovery-2025-ai-predictions',
    title: 'The Future of Brand Discovery: 2025 AI Search Predictions',
    description: 'Expert predictions on how AI-powered discovery will evolve in 2025. Prepare your brand for personalized recommendations, voice interfaces, and AI shopping agents.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-12-10',
    readingTime: '14 min read',
    category: 'AI Strategy',
    tags: ['AI Strategy', 'Predictions', 'Future Trends', '2025', 'Brand Discovery'],
    featured: true,
  },
  {
    slug: 'local-business-ai-visibility-roi-calculator',
    title: "The Local Business Owner's AI Visibility ROI Calculator",
    description: 'Calculate the real ROI of AI visibility investment for your local business. Framework, formulas, and examples by business type to build your business case.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-12-05',
    readingTime: '15 min read',
    category: 'Local Business',
    tags: ['ROI', 'Local Business', 'AI Visibility', 'Business Case', 'Calculator'],
    featured: false,
  },
  {
    slug: 'voice-search-ai-assistants-changing-consumer-behavior',
    title: 'How Voice Search and AI Assistants Are Changing Consumer Behavior',
    description: 'Voice + AI is transforming how consumers discover and choose businesses. Learn how to optimize for conversational, contextual, and voice-first discovery.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-11-30',
    readingTime: '13 min read',
    category: 'AI Strategy',
    tags: ['Voice Search', 'Consumer Behavior', 'AI Assistants', 'Siri', 'Alexa'],
    featured: false,
  },
  {
    slug: 'multi-location-brand-ai-visibility-strategy',
    title: 'Multi-Location Brand Strategy: AI Visibility Across Markets',
    description: 'Managing AI visibility for 10, 50, or 500 locations? Learn strategies for consistency, localization, and scaled optimization across all your markets.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-11-25',
    readingTime: '16 min read',
    category: 'AI Strategy',
    tags: ['Multi-Location', 'Franchise', 'Brand Strategy', 'AI Visibility', 'Local SEO'],
    featured: false,
  },
  // November 2024
  {
    slug: 'business-case-for-ai-visibility-optimization',
    title: 'The Business Case for AI Visibility Optimization',
    description: 'Build the internal business case for AI visibility investment. ROI framework, competitive analysis, and objection handling for executives and decision-makers.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-11-20',
    readingTime: '14 min read',
    category: 'AI Strategy',
    tags: ['Business Case', 'ROI', 'Executive', 'Investment', 'AI Visibility'],
    featured: false,
  },
  {
    slug: 'ecommerce-brands-winning-ai-powered-shopping',
    title: 'How E-commerce Brands Can Win in AI-Powered Shopping',
    description: 'AI is transforming product discovery and shopping. Learn how e-commerce brands can optimize product content, reviews, and presence for AI recommendations.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-11-15',
    readingTime: '15 min read',
    category: 'Industry Guides',
    tags: ['E-commerce', 'AI Shopping', 'Product Content', 'Reviews', 'Voice Commerce'],
    featured: false,
  },
  {
    slug: 'building-ai-first-content-strategy-framework-2025',
    title: 'Building an AI-First Content Strategy: Framework for 2025',
    description: 'Traditional content strategies fall short for AI visibility. Learn the four-layer content framework designed for AI-powered discovery and recommendations.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-11-10',
    readingTime: '15 min read',
    category: 'Content Strategy',
    tags: ['Content Strategy', 'AI-First', 'Framework', '2025', 'Content Marketing'],
    featured: true,
  },
  {
    slug: 'ai-visibility-for-saas-companies-complete-guide',
    title: 'AI Visibility for SaaS Companies: A Complete Guide',
    description: 'SaaS buyers increasingly rely on AI for software recommendations. Learn how to dominate G2, create comparison content, and win AI-powered software discovery.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-11-05',
    readingTime: '16 min read',
    category: 'Industry Guides',
    tags: ['SaaS', 'B2B', 'G2', 'Software', 'AI Visibility'],
    featured: false,
  },
  // October 2024
  {
    slug: 'role-of-citations-backlinks-ai-recommendations',
    title: 'The Role of Citations and Backlinks in AI Recommendations',
    description: 'How do backlinks and citations influence AI recommendations? Learn the difference between traditional SEO signals and AI-era authority building.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-10-30',
    readingTime: '13 min read',
    category: 'Technical Guides',
    tags: ['Backlinks', 'Citations', 'Authority', 'AI Recommendations', 'SEO'],
    featured: false,
  },
  {
    slug: 'schema-markup-for-ai-beyond-traditional-seo',
    title: 'Schema Markup for AI: Beyond Traditional SEO',
    description: 'Schema markup has evolved from SEO tactic to AI visibility essential. Learn which schema types matter most and how to implement them for AI discoverability.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-10-25',
    readingTime: '14 min read',
    category: 'Technical Guides',
    tags: ['Schema Markup', 'Structured Data', 'Technical SEO', 'AI Visibility', 'Implementation'],
    featured: false,
  },
  {
    slug: 'cracking-the-gemini-algorithm-google-ai-ecosystem',
    title: "Cracking the Gemini Algorithm: Google's AI Ecosystem Strategy",
    description: "Gemini integrates with Google Search, Maps, and Business Profile. Learn how to optimize for Google's AI ecosystem and dominate local AI recommendations.",
    author: 'BrandIndex AI Team',
    publishDate: '2024-10-20',
    readingTime: '15 min read',
    category: 'Platform Guides',
    tags: ['Gemini', 'Google', 'Google Business Profile', 'Local Search', 'AI Ecosystem'],
    featured: false,
  },
  {
    slug: 'claude-vs-chatgpt-how-to-rank-in-both',
    title: 'Claude vs ChatGPT: How to Rank in Both',
    description: 'ChatGPT and Claude have different architectures and recommendation patterns. Learn the key differences and how to build visibility in both AI platforms.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-10-15',
    readingTime: '14 min read',
    category: 'Platform Guides',
    tags: ['ChatGPT', 'Claude', 'Comparison', 'Optimization', 'AI Platforms'],
    featured: false,
  },
  {
    slug: '5-ai-optimization-mistakes-tank-brand-visibility',
    title: '5 AI Optimization Mistakes That Tank Your Brand Visibility',
    description: 'Some businesses optimize for AI visibility but get it wrong. Learn the five most damaging mistakes—from keyword stuffing to fake reviews—and how to avoid them.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-10-10',
    readingTime: '11 min read',
    category: 'AI Strategy',
    tags: ['Mistakes', 'AI Optimization', 'Common Errors', 'Best Practices', 'Brand Visibility'],
    featured: false,
  },
  // September 2024
  {
    slug: 'ai-visibility-audit-5-things-check-now',
    title: 'The AI Visibility Audit: 5 Things to Check Right Now',
    description: 'Not sure where you stand with AI visibility? Run this 30-minute audit to assess your current position and identify priority optimization areas.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-10-05',
    readingTime: '10 min read',
    category: 'How-To Guides',
    tags: ['Audit', 'Checklist', 'Quick Start', 'AI Visibility', 'Assessment'],
    featured: false,
  },
  {
    slug: 'complete-guide-optimizing-for-chatgpt',
    title: 'The Complete Guide to Optimizing for ChatGPT',
    description: 'ChatGPT has 100+ million weekly users. When it recommends businesses, it drives real decisions. Learn everything you need to appear in ChatGPT recommendations.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-09-30',
    readingTime: '15 min read',
    category: 'Platform Guides',
    tags: ['ChatGPT', 'OpenAI', 'Optimization', 'AI Recommendations', 'Complete Guide'],
    featured: true,
  },
  {
    slug: 'beating-national-brands-local-ai-search',
    title: 'Beating National Brands in Local AI Search: David vs. Goliath Tactics',
    description: 'Local businesses have advantages national chains cant replicate in AI search. Learn the David vs. Goliath tactics for winning local AI recommendations.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-09-25',
    readingTime: '13 min read',
    category: 'Local Business',
    tags: ['Local Business', 'Competition', 'National Brands', 'Local Search', 'Tactics'],
    featured: false,
  },
  {
    slug: 'what-local-businesses-get-wrong-about-ai-search',
    title: 'What Local Businesses Get Wrong About AI Search (And How to Fix It)',
    description: 'Most local businesses have misconceptions about AI search costing them customers. Learn the 10 most common mistakes and how to fix each one.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-09-20',
    readingTime: '12 min read',
    category: 'Local Business',
    tags: ['Local Business', 'Mistakes', 'Misconceptions', 'AI Search', 'Fixes'],
    featured: false,
  },
  {
    slug: 'home-services-ai-visibility-playbook',
    title: 'Home Services AI Visibility Playbook (Plumbers, Contractors, HVAC)',
    description: 'Emergency and high-intent queries dominate home services. Learn how plumbers, contractors, and HVAC companies can capture AI-driven leads.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-09-15',
    readingTime: '11 min read',
    category: 'Industry Guides',
    tags: ['Home Services', 'Plumbers', 'Contractors', 'HVAC', 'Local AI'],
    featured: false,
  },
  // August 2024
  {
    slug: 'restaurant-hospitality-guide-ai-dining-recommendations',
    title: 'Restaurant & Hospitality Guide: Winning in AI-Powered Dining Recommendations',
    description: 'AI is replacing Yelp and Google for dining decisions. Learn how restaurants can optimize for conversational dining queries and AI recommendations.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-09-10',
    readingTime: '12 min read',
    category: 'Industry Guides',
    tags: ['Restaurant', 'Hospitality', 'Dining', 'AI Recommendations', 'Local Business'],
    featured: false,
  },
  {
    slug: 'how-ai-assistants-recommend-local-service-providers',
    title: 'How AI Assistants Recommend Local Service Providers (Therapists, Lawyers, Doctors)',
    description: 'How does AI decide which therapist, lawyer, or doctor to recommend? Deep dive into authority signals, credentials, and specialization for service providers.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-09-05',
    readingTime: '13 min read',
    category: 'Industry Guides',
    tags: ['Service Providers', 'Therapists', 'Lawyers', 'Healthcare', 'AI Recommendations'],
    featured: false,
  },
  {
    slug: 'complete-local-business-guide-ai-search-optimization',
    title: 'The Complete Local Business Guide to AI Search Optimization',
    description: 'Comprehensive playbook for local businesses to dominate AI-powered discovery. From business scope to service areas to location-specific content strategies.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-08-30',
    readingTime: '18 min read',
    category: 'Local Business',
    tags: ['Local Business', 'Complete Guide', 'AI Optimization', 'Local SEO', 'Small Business'],
    featured: true,
  },
  {
    slug: 'why-local-businesses-need-ai-visibility',
    title: 'Why Local Businesses Need AI Visibility (More Than Enterprise Brands)',
    description: 'AI assistants are becoming the new phonebook for local recommendations. Learn why local businesses have unique advantages and opportunities in AI search.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-08-25',
    readingTime: '11 min read',
    category: 'Local Business',
    tags: ['Local Business', 'AI Visibility', 'Small Business', 'Enterprise', 'Market Opportunity'],
    featured: false,
  },
  // July 2024
  {
    slug: 'measure-ai-visibility-roi-metrics-that-matter',
    title: 'How to Measure AI Visibility ROI: Metrics That Actually Matter',
    description: 'Move beyond vanity metrics to measure real AI visibility ROI. Framework for tracking mention rate, accuracy, position, and business impact.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-08-20',
    readingTime: '12 min read',
    category: 'AI Strategy',
    tags: ['ROI', 'Metrics', 'Analytics', 'Measurement', 'AI Visibility'],
    featured: false,
  },
  {
    slug: 'geo-aeo-seo-understanding-new-optimization-landscape',
    title: 'GEO vs. AEO vs. SEO: Understanding the New Optimization Landscape',
    description: 'SEO isnt dead, but its not alone anymore. Understand Generative Engine Optimization, Answer Engine Optimization, and how they complement traditional SEO.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-08-15',
    readingTime: '13 min read',
    category: 'AI Strategy',
    tags: ['GEO', 'AEO', 'SEO', 'Optimization', 'Digital Marketing'],
    featured: false,
  },
  {
    slug: 'why-competitors-appear-chatgpt-you-dont',
    title: "Why Your Competitors Appear in ChatGPT and You Don't",
    description: 'Your competitors show up in AI recommendations while you remain invisible. Learn the specific signals they have that you are missing and how to close the gap.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-08-10',
    readingTime: '10 min read',
    category: 'AI Strategy',
    tags: ['Competition', 'ChatGPT', 'AI Visibility', 'Competitive Analysis', 'Gap Analysis'],
    featured: false,
  },
  {
    slug: '7-content-gaps-killing-ai-visibility',
    title: '7 Content Gaps Killing Your AI Visibility (And How to Fix Them)',
    description: 'Missing content means missing AI recommendations. Identify the seven critical content gaps that prevent AI from recommending your business.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-08-05',
    readingTime: '11 min read',
    category: 'Content Strategy',
    tags: ['Content Gaps', 'AI Visibility', 'Content Strategy', 'Optimization', 'Quick Fixes'],
    featured: false,
  },
  // June 2024 - Oldest Posts
  {
    slug: 'what-makes-content-visible-to-ai-models',
    title: 'What Makes Content Visible to AI Models?',
    description: 'Deep dive into the content factors that influence AI visibility. From structure and formatting to authority signals and freshness indicators.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-07-30',
    readingTime: '12 min read',
    category: 'Content Strategy',
    tags: ['Content', 'AI Models', 'Visibility Factors', 'Optimization', 'Technical'],
    featured: false,
  },
  {
    slug: 'how-each-ai-assistant-model-used-specialties',
    title: 'How Each AI Assistant/Model Is Used and Their Specialties',
    description: 'Comprehensive comparison of ChatGPT, Claude, Gemini, Perplexity, and other AI assistants. Understand each platform\'s strengths, use cases, and optimization priorities.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-07-25',
    readingTime: '14 min read',
    category: 'Platform Guides',
    tags: ['AI Assistants', 'ChatGPT', 'Claude', 'Gemini', 'Perplexity', 'Comparison'],
    featured: false,
  },
  {
    slug: '10-actions-position-brand-ai-assistant-search',
    title: '10 Actions to Best Position Your Brand for AI Assistant Search',
    description: 'Step-by-step checklist of 10 immediate actions to improve your brand visibility in ChatGPT, Claude, Gemini, and other AI assistants.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-07-20',
    readingTime: '15 min read',
    category: 'How-To Guides',
    tags: ['Action Plan', 'AI Optimization', 'Checklist', 'Brand Positioning', 'Quick Start'],
    featured: true,
  },
  {
    slug: 'how-ai-assistants-get-data-provide-recommendations',
    title: 'How AI Assistants Get Their Data and Provide Recommendations',
    description: 'Behind-the-scenes look at how ChatGPT, Claude, and Gemini source information and decide which businesses to recommend. Essential foundation for AI optimization.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-07-15',
    readingTime: '13 min read',
    category: 'AI Strategy',
    tags: ['AI Data', 'Recommendations', 'Training Data', 'How AI Works', 'Foundation'],
    featured: false,
  },
  {
    slug: 'llms-txt-complete-guide-business-owners',
    title: 'LLMS.txt: The Complete Guide for Business Owners',
    description: 'Everything you need to know about LLMS.txt files - what they are, why they matter for AI visibility, and how to create one for your business in minutes.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-07-10',
    readingTime: '10 min read',
    category: 'How-To Guides',
    tags: ['LLMS.txt', 'AI Optimization', 'Technical Guide', 'How-To', 'Website Optimization'],
    featured: false,
  },
  {
    slug: 'why-ai-visibility-matters-more-than-seo-2025',
    title: 'Why AI Visibility Matters More Than SEO in 2025',
    description: 'The way consumers discover businesses is changing. Learn why optimizing for AI assistants like ChatGPT and Claude is becoming more important than traditional SEO.',
    author: 'BrandIndex AI Team',
    publishDate: '2024-06-20',
    readingTime: '8 min read',
    category: 'AI Strategy',
    tags: ['AI Strategy', 'SEO', 'ChatGPT', 'Market Trends', 'Digital Marketing'],
    featured: false,
  },
];

// Get all posts
export function getAllPosts(): BlogPost[] {
  return blogPosts.sort((a, b) =>
    new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
  );
}

// Get post by slug
export function getPostBySlug(slug: string): BlogPost | undefined {
  return blogPosts.find(post => post.slug === slug);
}

// Get posts by category
export function getPostsByCategory(category: string): BlogPost[] {
  return blogPosts
    .filter(post => post.category === category)
    .sort((a, b) =>
      new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );
}

// Get featured posts
export function getFeaturedPosts(): BlogPost[] {
  return blogPosts
    .filter(post => post.featured)
    .sort((a, b) =>
      new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    );
}

// Get related posts (same category, excluding current post)
export function getRelatedPosts(slug: string, limit: number = 3): BlogPost[] {
  const currentPost = getPostBySlug(slug);
  if (!currentPost) return [];

  return blogPosts
    .filter(post =>
      post.slug !== slug &&
      post.category === currentPost.category
    )
    .sort((a, b) =>
      new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime()
    )
    .slice(0, limit);
}

// Get all categories
export function getAllCategories(): string[] {
  const categories = new Set(blogPosts.map(post => post.category));
  return Array.from(categories).sort();
}

// Format date for display
export function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
}

// Load markdown content for a blog post
export async function loadBlogPost(slug: string): Promise<string> {
  try {
    const response = await fetch(`/blog/${slug}.md`);
    if (!response.ok) {
      throw new Error(`Failed to load blog post: ${slug}`);
    }
    return await response.text();
  } catch (error) {
    console.error('Error loading blog post:', error);
    throw error;
  }
}
